import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { 
  Users, 
  MessageCircle, 
  Heart, 
  Lightbulb, 
  Calendar, 
  Award,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Music,
  Star,
  Quote
} from "lucide-react";
import communityPreview from "@/assets/community-preview.png";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";

const features = [
  {
    icon: Users,
    title: "Vernetze dich",
    description: "Tausche dich mit hunderten Gleichgesinnten aus der musikalischen Früherziehung aus."
  },
  {
    icon: Lightbulb,
    title: "Teile Ideen",
    description: "Poste deine Unterrichtsideen und lass dich von anderen inspirieren."
  },
  {
    icon: MessageCircle,
    title: "Stelle Fragen",
    description: "Erhalte schnelle Antworten von erfahrenen Kolleg:innen und unseren Dozent:innen."
  },
  {
    icon: Calendar,
    title: "Bleibe informiert",
    description: "Erfahre als Erstes von neuen Kursen, Webinaren und Events."
  },
  {
    icon: Heart,
    title: "Finde Support",
    description: "Eine unterstützende Gemeinschaft, die dich auf deinem Weg begleitet."
  },
  {
    icon: Award,
    title: "Exklusive Inhalte",
    description: "Zugang zu speziellen Materialien und Ressourcen nur für Community-Mitglieder."
  }
];

const testimonials = [
  {
    name: "Sandra M.",
    role: "Erzieherin aus Bayern",
    text: "Die Community ist für mich wie ein zweites Zuhause geworden. Der Austausch mit anderen ist unbezahlbar!",
    rating: 5
  },
  {
    name: "Thomas K.",
    role: "Musikpädagoge",
    text: "Hier bekomme ich immer hilfreiche Tipps und neue Inspiration für meine Arbeit.",
    rating: 5
  },
  {
    name: "Julia H.",
    role: "Kita-Leiterin",
    text: "Endlich eine Plattform, wo ich mich mit anderen Musikbegeisterten vernetzen kann!",
    rating: 5
  }
];

const stats = [
  { value: "500+", label: "Aktive Mitglieder" },
  { value: "1.000+", label: "Geteilte Ideen" },
  { value: "50+", label: "Wöchentliche Beiträge" },
  { value: "24/7", label: "Austausch" }
];

const CommunityLanding = () => {
  return (
    <div className="min-h-screen">
      <SEO title={`Community – Austausch für pädagogische Fachkräfte | MusiKonzept`} description={`Tausche dich mit anderen pädagogischen Fachkräften aus, teile Ideen und finde Inspiration in der MusiKonzept-Community.`} path="/community" />
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-[hsl(var(--gradient-start))] via-background to-[hsl(var(--gradient-end))] py-24 overflow-hidden">
          {/* Background decorations */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          </div>
          
          <div className="container mx-auto px-6 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8">
                <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 text-sm font-medium text-primary">
                  <Sparkles className="w-4 h-4" />
                  Exklusiv für Akademie-Mitglieder
                </div>
                
                <h1 className="text-5xl lg:text-6xl font-bold text-foreground leading-tight">
                  Werde Teil unserer 
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-accent to-primary"> Community</span>
                </h1>
                
                <p className="text-xl text-muted-foreground leading-relaxed">
                  Vernetze dich mit Gleichgesinnten, teile deine Ideen und lass dich inspirieren. 
                  Unsere Community ist der perfekte Ort für alle, die Musik in die Welt der Kinder bringen.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button size="lg" className="text-lg px-8" asChild>
                    <Link to="/preise">
                      Jetzt Mitglied werden
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Link>
                  </Button>
                  <Button size="lg" variant="outline" className="text-lg px-8" asChild>
                    <a href="#features">
                      Mehr erfahren
                    </a>
                  </Button>
                </div>
                
                {/* Trust indicators */}
                <div className="flex items-center gap-6 pt-4">
                  <div className="flex -space-x-3">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div
                        key={i}
                        className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 border-2 border-background flex items-center justify-center"
                      >
                        <Users className="w-4 h-4 text-primary" />
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">500+ Mitglieder</span> sind bereits dabei
                  </p>
                </div>
              </div>
              
              {/* Preview Image */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-accent/20 rounded-3xl blur-2xl transform rotate-3" />
                <div className="relative bg-card rounded-2xl shadow-2xl overflow-hidden border border-border/50">
                  <img 
                    src={communityPreview} 
                    alt="Community Vorschau" 
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                        <Music className="w-5 h-5 text-primary-foreground" />
                      </div>
                      <div>
                        <p className="font-semibold text-foreground">Aktiver Austausch</p>
                        <p className="text-sm text-muted-foreground">Täglich neue Beiträge</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-muted/30 border-y border-border/50">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent mb-2">
                    {stat.value}
                  </div>
                  <p className="text-muted-foreground font-medium">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-24">
          <div className="container mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl font-bold text-foreground mb-6">
                Was dich in der Community erwartet
              </h2>
              <p className="text-xl text-muted-foreground">
                Entdecke alle Vorteile unserer exklusiven MusiKonzept Community
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <Card 
                  key={index} 
                  className="p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-border/50 bg-card/50 backdrop-blur-sm"
                >
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mb-6">
                    <feature.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-24 bg-gradient-to-br from-primary/5 via-background to-accent/5">
          <div className="container mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl font-bold text-foreground mb-6">
                Das sagen unsere Mitglieder
              </h2>
              <p className="text-xl text-muted-foreground">
                Erfahre, warum unsere Community so besonders ist
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {testimonials.map((testimonial, index) => (
                <Card 
                  key={index} 
                  className="p-8 relative overflow-hidden hover:shadow-xl transition-all duration-300"
                >
                  <Quote className="absolute top-4 right-4 w-12 h-12 text-primary/10" />
                  
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-accent text-accent" />
                    ))}
                  </div>
                  
                  <p className="text-foreground mb-6 leading-relaxed italic">
                    "{testimonial.text}"
                  </p>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-bold">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">{testimonial.name}</p>
                      <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-24">
          <div className="container mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
              <div className="space-y-8">
                <h2 className="text-4xl font-bold text-foreground">
                  Alles, was du für deinen Erfolg brauchst
                </h2>
                <p className="text-xl text-muted-foreground leading-relaxed">
                  Mit deiner Mitgliedschaft erhältst du nicht nur Zugang zu unseren Kursen, 
                  sondern auch zur exklusiven Community mit all ihren Vorteilen.
                </p>
                
                <div className="space-y-4">
                  {[
                    "Unbegrenzter Zugang zur Community",
                    "Direkter Austausch mit Dozent:innen",
                    "Exklusive Materialien und Downloads",
                    "Networking mit Fachkräften deutschlandweit",
                    "Regelmäßige Live-Events und Q&As",
                    "Support bei Fragen rund um die Kurse"
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-6 h-6 text-primary flex-shrink-0" />
                      <span className="text-foreground">{benefit}</span>
                    </div>
                  ))}
                </div>
                
                <Button size="lg" className="text-lg px-8" asChild>
                  <Link to="/preise">
                    Mitgliedschaft entdecken
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
              </div>
              
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-accent/20 to-primary/20 rounded-3xl blur-2xl" />
                <Card className="relative p-8 border-2 border-primary/20 bg-card/80 backdrop-blur-sm">
                  <div className="text-center space-y-6">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-accent mx-auto flex items-center justify-center">
                      <Users className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-foreground">
                      Werde Teil der Familie
                    </h3>
                    <p className="text-muted-foreground">
                      Schließe dich unserer wachsenden Community an und erlebe, 
                      wie bereichernd der Austausch mit Gleichgesinnten sein kann.
                    </p>
                    <div className="pt-4 space-y-3">
                      <Button className="w-full" size="lg" asChild>
                        <Link to="/registrieren">
                          Kostenlos registrieren
                        </Link>
                      </Button>
                      <p className="text-sm text-muted-foreground">
                        Community-Zugang ist in allen Mitgliedschaften enthalten
                      </p>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-gradient-to-r from-primary via-primary/90 to-accent relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnY0em0wLTZ2LTRoLTJ2NGgyek0zNCAyNGgtMnY0aDJ2LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-20" />
          
          <div className="container mx-auto px-6 text-center relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Bereit, Teil unserer Community zu werden?
            </h2>
            <p className="text-xl text-white/80 max-w-2xl mx-auto mb-10">
              Starte noch heute und erlebe, wie bereichernd der Austausch 
              mit anderen Musikbegeisterten sein kann.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                variant="secondary"
                className="text-lg px-10 bg-white text-primary hover:bg-white/90"
                asChild
              >
                <Link to="/registrieren">
                  Jetzt starten
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="text-lg px-10 border-white text-white hover:bg-white/10"
                asChild
              >
                <Link to="/preise">
                  Preise ansehen
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CommunityLanding;
